import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { lessonsAPI } from '../services/api';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import TextField from '../components/UIKit/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LessonEditor from '../components/UIKit/LessonEditor';
import BlockSelectionEditor from '../components/BlockSelectionEditor';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { fetchSkills, selectAllSkills, selectSkillsLoading } from '../redux/slices/skillsSlice';

const RequestLessonChangesPage = () => {
  const { t } = useTranslation(['lessons', 'common']);
  const { lessonSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dispatch = useDispatch();
  
  // Redux селекторы для профессий и навыков
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const skills = useSelector(selectAllSkills);
  const skillsLoading = useSelector(selectSkillsLoading);
  
  // Получаем requestId из query параметров
  const searchParams = new URLSearchParams(location.search);
  const requestId = searchParams.get('requestId');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [originalLesson, setOriginalLesson] = useState(null);
  const [comment, setComment] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isEditingExistingRequest, setIsEditingExistingRequest] = useState(false);
  const [requestDataLoaded, setRequestDataLoaded] = useState(false);
  
  // Загружаем профессии и навыки при монтировании компонента
  useEffect(() => {
    if (professions.length === 0 && !professionsLoading) {
      dispatch(fetchProfessions({ all: true, isCommon: 1 }));
    }
    if (skills.length === 0 && !skillsLoading) {
      dispatch(fetchSkills({ all: true, isCommon: 1 }));
    }
  }, [dispatch, professions.length, professionsLoading, skills.length, skillsLoading]);
  
  // Загрузка данных урока
  useEffect(() => {
    const fetchLesson = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await lessonsAPI.getById(lessonSlug);
        
        if (response && response.data) {
          const lessonData = response.data.data;
          setLesson({...lessonData});
          setOriginalLesson({...lessonData});
        } else {
          setError(t('lessons:requestChanges.errors.notFound'));
        }
      } catch (err) {
        console.error('Ошибка при загрузке урока:', err);
        setError(
          err.response?.data?.message || t('lessons:requestChanges.errors.loadError')
        );
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
  }, [lessonSlug, t]);

  // Загрузка данных из существующего запроса, если есть requestId
  useEffect(() => {
    const fetchExistingRequest = async () => {
      // Предотвращаем повторные вызовы
      if (requestDataLoaded || !user || !lesson) {
        return;
      }

      // Если нет requestId, но есть активный запрос для этого урока, найдем его
      if (!requestId) {
        try {
          const response = await lessonsAPI.getUserChangeRequests({ page: 1, limit: 100 });
          
          if (response && response.data) {
            const activeRequest = response.data.data.find(req => 
              req.lesson._id === lesson?._id && req.status === 'pending'
            );
            
            if (activeRequest) {
              // Обновляем URL с requestId
              const newUrl = `/request-lesson-changes/${lessonSlug}?requestId=${activeRequest._id}`;
              navigate(newUrl, { replace: true });
              return;
            }
          }
        } catch (err) {
          console.error('Error checking for active request:', err);
        }
        return;
      }
      
      try {
        setIsEditingExistingRequest(true);
        
        const response = await lessonsAPI.getMyChangeRequestById(requestId);
        
        if (response && response.data) {
          const requestData = response.data.data;
          
          // Загружаем данные из существующего запроса
          if (requestData.proposedChanges) {
            setLesson({
              ...lesson,
              ...requestData.proposedChanges
            });
          }
          
          if (requestData.comment) {
            setComment(requestData.comment);
          }
          
          if (requestData.selectedBlocks) {
            setSelectedBlocks(requestData.selectedBlocks);
          }
          
          setRequestDataLoaded(true);
        }
      } catch (err) {
        console.error('RequestLessonChangesPage: Error loading existing request:', err);
        toast.error('Ошибка при загрузке существующего запроса');
      }
    };

    fetchExistingRequest();
  }, [requestId, user, lesson, lessonSlug, navigate, requestDataLoaded]);

  // Обработчик отправки запроса на изменение
  const handleSubmitChanges = async () => {
    try {
      setSaving(true);
      
      // Проверка на наличие изменений
      if (!hasChanges) {
        toast.error(t('lessons:requestChanges.validation.noChanges'));
        setSaving(false);
        return;
      }
      
      // Проверка выбранных блоков
      if (selectedBlocks.length === 0) {
        toast.error(t('lessons:requestChanges.validation.noBlocksSelected'));
        setSaving(false);
        return;
      }
      
      // Проверка комментария
      if (!comment.trim()) {
        toast.error(t('lessons:requestChanges.validation.commentRequired'));
        setSaving(false);
        return;
      }

      // Создаем объект изменений на основе выбранных блоков
      const proposedChanges = createProposedChangesFromSelectedBlocks();
      
      if (isEditingExistingRequest && requestId) {
        // Создаем правильную структуру для обновления запроса
        const updateData = {
          proposedChanges,
          comment,
          selectedBlocks
        };
        
        
        await lessonsAPI.updateMyChangeRequest(requestId, updateData);
        toast.success(t('lessons:page.requestUpdateSuccess'));
        
        // Возвращаемся к списку запросов
        navigate('/my-change-requests');
      } else {
        // Создаем новый запрос на изменение
        await lessonsAPI.createChangeRequest(lesson._id, {
          proposedChanges,
          comment,
          selectedBlocks
        });
        
        toast.success(t('lessons:requestChanges.submitSuccess'));
        
        // Возвращаемся на страницу просмотра урока
        navigate(`/lessons/${lessonSlug}`);
      }
    } catch (err) {
      console.error('Ошибка при отправке запроса на изменение урока:', err);
      toast.error(
        err.response?.data?.message || t('common:errors.savingFailed')
      );
    } finally {
      setSaving(false);
    }
  };

  // Функция для создания объекта изменений на основе выбранных блоков
  const createProposedChangesFromSelectedBlocks = () => {
    const changes = {};
    
    selectedBlocks.forEach(blockId => {
      if (blockId === 'main_content') {
        // Основной контент
        changes.content = lesson.content;
        changes.contentType = lesson.contentType;
      } else if (blockId.startsWith('contentBlock_')) {
        // Контент-блоки
        const blockIndex = parseInt(blockId.replace('contentBlock_', ''));
        if (!changes.contentBlocks) {
          changes.contentBlocks = [...(originalLesson.contentBlocks || [])];
        }
        changes.contentBlocks[blockIndex] = lesson.contentBlocks[blockIndex];
      }
    });
    
    return changes;
  };

  // Обработчик для возврата на страницу урока
  const handleBackToLesson = () => {
    // Если есть изменения, показать диалоговое окно для подтверждения
    if (hasChanges) {
      setConfirmDialogOpen(true);
    } else {
      navigate(`/lessons/${lessonSlug}`);
    }
  };

  const handleConfirmBack = () => {
    setConfirmDialogOpen(false);
    navigate(`/lessons/${lessonSlug}`);
  };

  const handleCancelBack = () => {
    setConfirmDialogOpen(false);
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!lesson) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">{t('lessons:requestChanges.lessonNotFound')}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          {isEditingExistingRequest 
            ? t('lessons:requestChanges.editTitle') 
            : t('lessons:requestChanges.title')
          }
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToLesson}
            sx={{ mr: 2 }}
          >
            {t('common:actions.back')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmitChanges}
            disabled={saving}
          >
            {saving
              ? t('lessons:requestChanges.buttons.submitting')
              : t('lessons:requestChanges.buttons.submit')}
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('lessons:requestChanges.description')}
      </Typography>

      {/* Інформація про автоматичне виявлення змін */}
      {hasChanges && selectedBlocks.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('lessons:blockSelection.autoSelectionInfo', { 
            count: selectedBlocks.length 
          })}
        </Alert>
      )}

      {!hasChanges && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('lessons:blockSelection.noChangesDetected')}
        </Alert>
      )}

      <LessonEditor
        lesson={lesson}
        onChange={setLesson}
        header={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" component="h2">
              {lesson.title}
            </Typography>
          </Box>
        }
        isAdminOrTeacher={
          (user.userMeta?.roles || user.roles || []).includes('admin') ||
          (user.userMeta?.roles || user.roles || []).includes('teacher')
        }
        showTestsTab={false}
      />

      {/* BlockSelectionEditor працює в фоновому режимі для автоматичного виявлення змін */}
      <Box sx={{ display: 'none' }}>
        <BlockSelectionEditor
          lesson={lesson}
          originalLesson={originalLesson}
          selectedBlocks={selectedBlocks}
          onBlockSelectionChange={setSelectedBlocks}
          onChangesDetected={setHasChanges}
        />
      </Box>

      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('lessons:requestChanges.commentLabel')}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('lessons:requestChanges.commentPlaceholder')}
          helperText={t('lessons:requestChanges.commentHelperText')}
          required
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 4,
        }}
      >
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmitChanges}
          disabled={saving}
          size="large"
        >
          {saving
            ? t('lessons:requestChanges.buttons.submitting')
            : t('lessons:requestChanges.buttons.submit')}
        </Button>
      </Box>

      {/* Диалоговое окно для подтверждения выхода без сохранения */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelBack}
      >
        <DialogTitle>{t('lessons:requestChanges.confirmDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('lessons:requestChanges.confirmDialog.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBack} color="primary">
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleConfirmBack} color="error">
            {t('common:actions.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RequestLessonChangesPage; 